import { useEffect, useRef } from "react";
import FormHeader from "../FormHeader";
import { Col, Form, Input, Radio, Row, Select, Table } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import InfoPopover from "@/components/ui/InfoPopover";
import IconButton from "@/components/ui/IconButton";
import {
  TIndexLookUpApproach,
  TradedFundFilters,
  TFundType,
  TRecommendationType,
  TradedFundsPlaceholderName,
} from "../../../types/newLinkBuilder";
import { fundTypeOptions } from "./NewLinkBuilder";
import TagsInputField from "@/components/TagsInputField";
import { useStyles } from "@/components/style";

const infoPopoverContent = {
  indexLookUpApproach: (
    <span>
      We offer three approaches to look up an Index or ETF:
      <ol>
        <li>
          <strong>Recommendation Tool:</strong> Select a company + Recommendation Type you are looking for. We will
          provide an Index / ETF recommendation list based on available data.
        </li>
        <li>
          <strong>Search Tool:</strong> If you know the Index or ETF you are looking for, search by product name.
        </li>
        <li>
          <strong>Cell Reference:</strong> If you have the Traded Fund exchange and ticker referenced in your Excel or
          know it already, included it directly here and move on swiftly.
        </li>
      </ol>
      Select one and complete the rest of the form to create an Alpha Formula for a specific Index / ETF.
    </span>
  ),
  recommendationTool: (
    <span>
      Enter a company name and{" "}
      <strong>we return a recommended list of Traded Funds that serve as relevant market trading proxies.</strong>{" "}
      Database is limited and most robust with broad market indices (U.S. and International), U.S. sector verticals and
      technology industry sector sub verticals
    </span>
  ),
  searchByFundName: (
    <span>Provides access to our full database of Index Funds, Mutual Funds, and ETFs. Search by Fund Name.</span>
  ),
  nicheOrTheme: <span>Compare the company to a focused thematic or sub-industry cohort.</span>,
  market: <span>Compare the company to the overall market it trades within..</span>,
  sector: <span>Compare the company to its sector/industry peers.</span>,
  etf: (
    <span>
      ETF ranking based on various factors include market cap, 100-Day average traded volume, issuer reputation and ETF
      inception date.
    </span>
  ),
};

const IndexLookUpApproach = ({
  indexLookUpApproach,
  setIndexLookUpApproach,
}: {
  indexLookUpApproach: TIndexLookUpApproach;
  setIndexLookUpApproach: (value: TIndexLookUpApproach) => void;
}) => {
  const handleRadioChange = (e: any) => {
    setIndexLookUpApproach(e.target.value as TIndexLookUpApproach);
  };

  return (
    <Form.Item>
      <div style={{ paddingBottom: "10px" }}>
        <label style={{ fontWeight: "bold" }}>
          Choose Traded Fund Look-up Approach:{" "}
          <InfoPopover
            content={infoPopoverContent.indexLookUpApproach}
            trigger="hover"
            placement="bottom"
            overlayStyle={{ width: 400, borderRadius: "4px" }}
          />
        </label>
      </div>
      <div>
        <Radio.Group
          value={indexLookUpApproach}
          onChange={handleRadioChange}
          style={{ display: "flex", flexDirection: "column", gap: "10px" }}
        >
          <Radio name="indexLookUpApproach" value={TIndexLookUpApproach.RecommendationTool}>
            Recommendation Tool{" "}
            <InfoPopover content={infoPopoverContent.recommendationTool} trigger="hover" placement="bottom" />
          </Radio>
          <Radio name="indexLookUpApproach" value={TIndexLookUpApproach.SearchByFundName}>
            Search by Fund Name{" "}
            <InfoPopover content={infoPopoverContent.searchByFundName} trigger="hover" placement="bottom" />
          </Radio>
          <Radio name="indexLookUpApproach" value={TIndexLookUpApproach.CellReference}>
            Cell Reference
          </Radio>
        </Radio.Group>
      </div>
    </Form.Item>
  );
};

const CellReferenceInputs = ({
  exchange,
  setExchange,
  ticker,
  setTicker,
  handlePlaceholder,
}: {
  exchange: string;
  setExchange: (value: string) => void;
  ticker: string;
  setTicker: (value: string) => void;
  handlePlaceholder: (name: TradedFundsPlaceholderName) => void;
}) => {
  return (
    <div>
      <Form.Item name="index">
        <Row align="middle">
          <Col span={6} style={{ display: "flex", justifyContent: "start" }}>
            <label style={{ fontWeight: "bold" }}>Cell Reference</label>
          </Col>
          <Col span={18} style={{ display: "flex", justifyContent: "start", flexDirection: "column", gap: "10px" }}>
            <Row align="middle">
              <Col span={6} style={{ display: "flex", justifyContent: "start" }}>
                <span>- Exchange</span>
              </Col>
              <Col span={16} style={{ display: "flex", justifyContent: "start" }}>
                <Input placeholder="Exchange" value={exchange} onChange={(e) => setExchange(e.target.value)} />
              </Col>
              <Col span={2} style={{ display: "flex", justifyContent: "end" }}>
                <IconButton
                  icon={<UploadOutlined />}
                  onClick={() => handlePlaceholder(TradedFundsPlaceholderName.FundIdentificationExchangeCellReference)}
                />
              </Col>
            </Row>
            <Row align="middle">
              <Col span={6} style={{ display: "flex", justifyContent: "start" }}>
                <span>- Ticker</span>
              </Col>
              <Col span={16} style={{ display: "flex", justifyContent: "start" }}>
                <Input placeholder="Ticker" value={ticker} onChange={(e) => setTicker(e.target.value)} />
              </Col>
              <Col span={2} style={{ display: "flex", justifyContent: "end" }}>
                <IconButton
                  icon={<UploadOutlined />}
                  onClick={() => handlePlaceholder(TradedFundsPlaceholderName.FundIdentificationTickerCellReference)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form.Item>
    </div>
  );
};
const Search = ({
  indexLookUpApproach,
  searchValue,
  setSearchValue,
  setIsRestoring,
}: {
  indexLookUpApproach: TIndexLookUpApproach;
  searchValue: string;
  setSearchValue: (value: string) => void;
  setIsRestoring: (value: boolean) => void;
}) => {
  return (
    <Form.Item>
      <Row style={{ display: "flex", alignItems: "center" }}>
        <label style={{ flex: 1, fontWeight: "bold" }} htmlFor="search">
          {indexLookUpApproach === TIndexLookUpApproach.RecommendationTool ? "Company Search" : "Fund Name Search"}
        </label>
        {indexLookUpApproach === TIndexLookUpApproach.RecommendationTool ? (
          <TagsInputField
            style={{ flex: 3 }}
            placeholder="Search"
            onChange={(value) => {
              setIsRestoring(false);
              if (value) {
                const symbol = JSON.parse(value).symbol;
                setSearchValue(symbol);
              } else {
                setSearchValue("");
              }
            }}
          />
        ) : (
          <Input
            style={{ flex: 3 }}
            placeholder="Search"
            value={searchValue}
            onChange={(e) => {
              setIsRestoring(false);
              setSearchValue(e.target.value);
            }}
          />
        )}
      </Row>
    </Form.Item>
  );
};

const TradedFundFilters = ({
  indexLookUpApproach,
  tradedFundFilters,
  setTradedFundFilters,
  setSearchValue,
  setIndex,
  setRecommendedTradedFunds,
  isRestoring,
  setIsRestoring,
}: {
  indexLookUpApproach: TIndexLookUpApproach;
  tradedFundFilters: TradedFundFilters;
  setTradedFundFilters: (value: TradedFundFilters) => void;
  setSearchValue: (value: string) => void;
  setIndex: (value: string) => void;
  setRecommendedTradedFunds: (value: any) => void;
  isRestoring: boolean;
  setIsRestoring: (value: boolean) => void;
}) => {
  const firstRenderRef = useRef(true);
  const alreadyCheckedRef = useRef(false);
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (isRestoring && !alreadyCheckedRef.current) {
      alreadyCheckedRef.current = true;
      return;
    }
    if (tradedFundFilters.fundType === "mutualFund") {
      setTradedFundFilters({ ...tradedFundFilters, fundType: fundTypeOptions[indexLookUpApproach][0].value });
    }
    setSearchValue("");
    setIndex("");
    setRecommendedTradedFunds([]);
  }, [indexLookUpApproach]);

  return (
    <Form.Item>
      <div style={{ paddingBottom: "10px" }}>
        <label style={{ fontWeight: "bold" }}>Traded Fund Filters</label>
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <label style={{ flex: 2 }} htmlFor="fundType">
            Fund Type
          </label>
          <Select
            id="fundType"
            value={tradedFundFilters.fundType}
            onChange={(value) => {
              setIsRestoring(false);
              setTradedFundFilters({ ...tradedFundFilters, fundType: value });
            }}
            options={[...fundTypeOptions[indexLookUpApproach]]}
            style={{ width: "110px" }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <label style={{ flex: 2 }} htmlFor="region">
            Region
          </label>
          <Select
            id="region"
            value={tradedFundFilters.region}
            onChange={(value) => {
              setIsRestoring(false);
              setTradedFundFilters({ ...tradedFundFilters, region: value });
            }}
            options={[
              { value: "global", label: "Global" },
              { value: "us", label: "U.S." },
              { value: "international", label: "International" },
            ]}
            style={{ width: "110px" }}
          />
        </div>

        {tradedFundFilters.fundType !== TFundType.Index && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <label style={{ flex: 2 }} htmlFor="maxResults">
              Max Results
            </label>
            <Input
              id="maxResults"
              value={tradedFundFilters.maxResults}
              onChange={(e) => {
                setIsRestoring(false);
                setTradedFundFilters({ ...tradedFundFilters, maxResults: Number(e.target.value) });
              }}
              type="number"
              style={{ width: "110px" }}
            />
          </div>
        )}
      </div>
    </Form.Item>
  );
};

const RecommededTradedFunds = ({
  index,
  setIndex,
  recommendedTradedFunds,
  searchLoading,
  fundType,
}: {
  index: string;
  setIndex: (value: string) => void;
  recommendedTradedFunds: any;
  searchLoading: boolean;
  fundType: string;
}) => {
  const selectColumn = {
    title: "Select",
    dataIndex: "symbol",
    key: "select",
    width: 60,
    render: (symbol: string) => (
      <Radio
        checked={index === symbol}
        onChange={() => {
          setIndex(symbol || "");
        }}
      />
    ),
  } as const;

  const indexColumns = [
    selectColumn,
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
  ];

  const etfColumns = [
    selectColumn,
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 100,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 80,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Top 3 holdings",
      key: "top3Holdings",
      width: 225,
      render: (_: any, record: any) => {
        const holdings = Array.isArray(record?.top3Holdings) ? record.top3Holdings.slice(0, 3) : [];
        return (
          <div>
            {holdings.length === 0 ? (
              <span></span>
            ) : (
              <ol style={{ padding: 0, margin: 0 }}>
                {holdings.map((h: any, i: number) => {
                  const percent = h.asset_percentage;
                  const formatted =
                    typeof percent === "number" ? `${percent.toFixed(2)}%` : typeof percent === "string" ? percent : "";
                  return (
                    <li key={`${record.symbol}-h-${i}`}>{`${h?.name || ""}${formatted ? ` – ${formatted}` : ""}`}</li>
                  );
                })}
              </ol>
            )}
          </div>
        );
      },
    },
  ];

  const columns = fundType === TFundType.Index ? indexColumns : etfColumns;

  return (
    <Form.Item style={{ width: "100%" }} name="index">
      <div style={{ paddingBottom: "10px", marginTop: "10px" }}>
        {fundType === TFundType.Index ? (
          <label style={{ fontWeight: "bold" }}>Recommended Traded Funds</label>
        ) : (
          <label style={{ fontWeight: "bold" }}>
            Recommended ETFs <InfoPopover content={infoPopoverContent.etf} trigger="hover" placement="bottom" />
          </label>
        )}
      </div>
      <div style={{ width: "100%" }}>
        <Table
          columns={columns}
          dataSource={recommendedTradedFunds}
          pagination={false}
          size="small"
          tableLayout="auto"
          rowKey="symbol"
          loading={searchLoading}
          locale={{ emptyText: searchLoading ? "Loading..." : "No results found" }}
        />
      </div>
    </Form.Item>
  );
};

const RecommendationType = ({
  recommendationType,
  setRecommendationType,
  setIsRestoring,
}: {
  recommendationType: TRecommendationType;
  setRecommendationType: (value: TRecommendationType) => void;
  setIsRestoring: (value: boolean) => void;
}) => {
  const styles = useStyles();
  return (
    <Form.Item>
      <Row style={{ display: "flex", alignItems: "center" }}>
        <Col span={6}>
          <label style={{ fontWeight: "bold" }} htmlFor="search">
            Recommendation Type
          </label>
        </Col>
        <Col span={18}>
          <Radio.Group
            value={recommendationType}
            onChange={(e) => {
              setIsRestoring(false);
              setRecommendationType(e.target.value as TRecommendationType);
            }}
          >
            <Row className={styles.tradedFundsRecommendationTypeRadio}>
              <Col span={8}>
                <Radio
                  value={TRecommendationType.Niche}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}
                >
                  <div>
                    Niche/Theme{" "}
                    <InfoPopover content={infoPopoverContent.nicheOrTheme} trigger="hover" placement="bottom" />
                  </div>
                  <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.65)", marginTop: 4 }}>(Tightest Peer Set)</span>
                </Radio>
              </Col>
              <Col span={8}>
                <Radio
                  value={TRecommendationType.Sector}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}
                >
                  <div>
                    Sector <InfoPopover content={infoPopoverContent.sector} trigger="hover" placement="bottom" />
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.65)", marginTop: 4 }}>(Economic Grouping)</div>
                </Radio>
              </Col>
              <Col span={8}>
                <Radio
                  value={TRecommendationType.Market}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}
                >
                  <div>
                    Market <InfoPopover content={infoPopoverContent.market} trigger="hover" placement="bottom" />
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.65)", marginTop: 4 }}>(Broad Beta)</div>
                </Radio>
              </Col>
            </Row>
          </Radio.Group>
        </Col>
      </Row>
    </Form.Item>
  );
};

const FundIdentification = ({
  tradedFundsIdentification,
  setTradedFundsIdentification,
  tradedFundFilters,
  setTradedFundFilters,
  searchValue,
  setSearchValue,
  recommendedTradedFunds,
  searchLoading,
  setRecommendedTradedFunds,
  recommendationType,
  setRecommendationType,
  isRestoring,
  setIsRestoring,
  handlePlaceholder,
}: {
  tradedFundsIdentification: { selected: TIndexLookUpApproach; index?: string; exchange?: string; ticker?: string };
  setTradedFundsIdentification: (updater: any) => void;
  tradedFundFilters: TradedFundFilters;
  setTradedFundFilters: (value: TradedFundFilters) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  recommendedTradedFunds: any;
  searchLoading: boolean;
  setRecommendedTradedFunds: (value: any) => void;
  recommendationType: TRecommendationType;
  setRecommendationType: (value: TRecommendationType) => void;
  isRestoring: boolean;
  setIsRestoring: (value: boolean) => void;
  handlePlaceholder: (name: TradedFundsPlaceholderName) => void;
}) => {
  return (
    <div>
      <FormHeader title="Fund Identification" />
      <Row>
        <Col span={12}>
          <IndexLookUpApproach
            indexLookUpApproach={tradedFundsIdentification.selected}
            setIndexLookUpApproach={(v) => setTradedFundsIdentification((prev: any) => ({ ...prev, selected: v }))}
          />
        </Col>
        {tradedFundsIdentification.selected !== TIndexLookUpApproach.CellReference && (
          <Col span={12}>
            <TradedFundFilters
              indexLookUpApproach={tradedFundsIdentification.selected}
              tradedFundFilters={tradedFundFilters}
              setTradedFundFilters={setTradedFundFilters}
              setSearchValue={setSearchValue}
              setIndex={(v) => setTradedFundsIdentification((prev: any) => ({ ...prev, index: v }))}
              setRecommendedTradedFunds={setRecommendedTradedFunds}
              isRestoring={isRestoring}
              setIsRestoring={setIsRestoring}
            />
          </Col>
        )}
      </Row>
      {tradedFundsIdentification.selected === TIndexLookUpApproach.CellReference ? (
        <CellReferenceInputs
          exchange={tradedFundsIdentification.exchange || ""}
          setExchange={(v) => setTradedFundsIdentification((prev: any) => ({ ...prev, exchange: v }))}
          ticker={tradedFundsIdentification.ticker || ""}
          setTicker={(v) => setTradedFundsIdentification((prev: any) => ({ ...prev, ticker: v }))}
          handlePlaceholder={handlePlaceholder}
        />
      ) : (
        <>
          <Search
            indexLookUpApproach={tradedFundsIdentification.selected}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setIsRestoring={setIsRestoring}
          />
          {tradedFundsIdentification.selected === TIndexLookUpApproach.RecommendationTool && (
            <RecommendationType
              recommendationType={recommendationType}
              setRecommendationType={setRecommendationType}
              setIsRestoring={setIsRestoring}
            />
          )}
          {searchValue && (
            <RecommededTradedFunds
              index={tradedFundsIdentification.index || ""}
              setIndex={(v) => setTradedFundsIdentification((prev: any) => ({ ...prev, index: v }))}
              recommendedTradedFunds={recommendedTradedFunds}
              searchLoading={searchLoading}
              fundType={tradedFundFilters.fundType}
            />
          )}
        </>
      )}
    </div>
  );
};

export default FundIdentification;
